import {
  ElementRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Select, { MenuPlacement } from "react-select";
import { DropdownProps, MultiSelectDropdownProps, SizeVariant } from "./types";

type FromDropdownOrMultiSelectDropdownProps<
  Key extends keyof DropdownProps & keyof MultiSelectDropdownProps
> = DropdownProps[Key] | MultiSelectDropdownProps[Key];

type DropdownDefaultValue =
  FromDropdownOrMultiSelectDropdownProps<"defaultValue">;
type DropdownOnClear = FromDropdownOrMultiSelectDropdownProps<"onClear">;

type useSelectionStateArgs<T extends DropdownDefaultValue> = {
  defaultValue?: T;
  value?: T;
};

const useSelectionState = <T extends DropdownDefaultValue>({
  defaultValue,
  value,
}: useSelectionStateArgs<T>) => {
  const [selected, setSelected] = useState(defaultValue);
  const clearSelected = useCallback(() => {
    setSelected(undefined);
  }, [setSelected]);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  return { selected, setSelected, clearSelected };
};

type useMenuOpenCloseArgs = {
  selectRef: MutableRefObject<ElementRef<typeof Select> | null>;
};

const useMenuOpenClose = ({ selectRef }: useMenuOpenCloseArgs) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const openMenu = useCallback(() => setMenuIsOpen(true), [setMenuIsOpen]);
  const closeMenu = useCallback(() => setMenuIsOpen(false), [setMenuIsOpen]);

  useEffect(() => {
    if (menuIsOpen) {
      selectRef.current?.focus();
    } else {
      selectRef.current?.blur();
    }
  }, [menuIsOpen, selectRef]);

  return { menuIsOpen, openMenu, closeMenu };
};

type useDropdownArgs<T extends DropdownDefaultValue> = Required<
  Pick<DropdownProps, "options" | "name" | "variant" | "placeholder">
> & {
  defaultValue?: T;
  value?: T;
  onClear: DropdownOnClear;
  menuPlacement?: MenuPlacement;
  size?: SizeVariant;
};

export const useDropdown = <T extends DropdownDefaultValue>({
  defaultValue,
  value,
  name,
  onClear,
  options,
  placeholder,
  variant,
  menuPlacement,
  size,
  ...selectProps
}: useDropdownArgs<T>) => {
  const { selected, setSelected, clearSelected } = useSelectionState({
    defaultValue,
    value,
  });
  const selectRef = useRef<ElementRef<typeof Select>>(null);
  const { menuIsOpen, openMenu, closeMenu } = useMenuOpenClose({
    selectRef,
  });

  const dropdownProps = {
    defaultValue: selected,
    isClearable: typeof onClear !== "undefined",
    isDisabled: variant === "DISABLED" || variant === "READONLY",
    isSearchable: true,
    menuIsOpen,
    name,
    onMenuOpen: openMenu,
    onMenuClose: closeMenu,
    options,
    placeholder,
    ref: selectRef,
    menuPlacement,
    size,
    ...selectProps,
  };

  return {
    selected,
    setSelected,
    clearSelected,
    dropdownProps,
  };
};
